/**
 * Message Utilities — Pure functions extracted VERBATIM from SimpleCopilotPanel.tsx
 * 
 * Shared by: useMessageSender, useAdaptiveCardHandler, useFormHandler
 * Risk: R18 (cross-dep) — mitigated via formUtils import
 * 
 * SAFE REFACTORING: No logic changes. Functions copied exactly as-is.
 * 
 * NOTE: createUserMessage takes uploadedFiles as explicit param (was closure ref in SCP).
 * Callers pass the same value — behavior identical.
 */

import { COPILOT_CONSTANTS } from '../constants';
import type { Message } from '../interfaces';
import { normalizeFormFields } from './formUtils';

/**
 * Create a user message object
 * Verbatim from SimpleCopilotPanel.tsx L930-942
 * 
 * Change: `uploadedFiles` was a closure variable — now an explicit parameter.
 * Callers pass the same state value, so behavior is identical.
 */
export const createUserMessage = (question: string, hasFiles: boolean, uploadedFiles: File[]): Message => {
  const filesSummary = hasFiles && !question ? `uploading files...` : '';
  return {
    id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.USER + Date.now(),
    role: 'user',
    content: question + filesSummary,
    timestamp: Date.now(),
    meta: {
      fileCount: uploadedFiles.length,
      fileNames: uploadedFiles.map(f => f.name)
    }
  };
};

/**
 * Create a bot/assistant message object
 * Verbatim from SimpleCopilotPanel.tsx L990-1026
 */
export const createBotMessage = (answerText: string, data: any, formatted: any): Message => {
  // Check if data itself is already an Adaptive Card
  const isAdaptiveCard = data && 
    typeof data === 'object' && 
    data.type === 'AdaptiveCard' && 
    data.version;
  
  // Clean up Adaptive Card if needed
  if (isAdaptiveCard) {
    // Clean up null/undefined actions property
    if (data.actions === null || data.actions === undefined) {
      delete data.actions;
    }
  }

  // ========== BUILD MESSAGE OBJECT ==========
  return {
    id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ASSISTANT + Date.now(),
    role: 'assistant',
    content: answerText, // Empty string for Adaptive Cards, text for regular responses
    timestamp: Date.now(),
    meta: {
      sources: data.sources,
      followUpQuestions: data.followUpQuestions || data.followupQuestions || undefined,
      ticketGuidance: data.ticketGuidance || data.ticketGuidence || undefined,
      responseType: isAdaptiveCard ? 'adaptiveCard' : (formatted.format?.toLowerCase().includes("chart") ? "graph" : formatted.format),
      dataToDisplay: formatted.dataToDisplay,
      formFields: formatted.format === 'form' ? normalizeFormFields(formatted.dataToDisplay) : undefined,
      formTitle: formatted.format === 'form' ? formatted.dataToDisplay.formTitle : undefined,
      formDescription: formatted.format === 'form' ? formatted.dataToDisplay.formDescription : undefined,
      formSessionActive: data.formSessionActive || false,
      confidence: data.confidence,
      processingTime: data.processingTime,
      adaptiveCard: isAdaptiveCard ? data : undefined // Store the actual Adaptive Card object directly
    }
  };
};
